import { useCallback, useEffect, useMemo } from 'react';
// import PropTypes from 'prop-types';
import { Autocomplete, AutocompleteProps, createFilterOptions } from '@mui/material';
import { unstable_useId as useId } from '@mui/utils';
import { GridFilterInputValueProps, ValueOptions, useGridRootProps } from '@mui/x-data-grid';

import {
  GridMultiSelectColDef,
  defGetOptionLabel,
  defGetOptionValue,
  isMultiSelectColDef,
} from './GridEditMultiSelectCell';

export interface GridFilterInputMultipleSingleSelectProps
  extends Omit<
      AutocompleteProps<ValueOptions, true, false, true>,
      | 'options'
      | 'renderInput'
      | 'onChange'
      | 'value'
      | 'id'
      | 'filterOptions'
      | 'isOptionEqualToValue'
      | 'multiple'
      | 'color'
      | 'getOptionLabel'
    >,
    Pick<GridMultiSelectColDef, 'getOptionLabel' | 'getOptionValue'>,
    GridFilterInputValueProps {
  type?: 'multiSelect';
}

const filter = createFilterOptions<any>();

export const GridFilterInputMultipleMultipleSelect = (
  props: GridFilterInputMultipleSingleSelectProps
) => {
  console.log('PROPS: ', props);
  const {
    item,
    applyValue,
    type,
    apiRef,
    focusElementRef,
    color,
    error,
    helperText,
    size,
    variant = 'standard',
    getOptionLabel: getOptionLabelProp,
    getOptionValue: getOptionValueProp,
    ...other
  } = props;
  const TextFieldProps = {
    color,
    error,
    helperText,
    size,
    variant,
  };

  const id = useId();
  const rootProps = useGridRootProps();

  let resolvedColumn: GridMultiSelectColDef | null = null;
  if (item.field) {
    const column = apiRef?.current?.getColumn(item.field);
    console.log('COLUMN: ', column);
    if (isMultiSelectColDef(column)) {
      resolvedColumn = column;
    }
  }

  const getOptionValue = getOptionValueProp || defGetOptionValue; // || resolvedColumn?.getOptionValue!;
  const getOptionLabel = getOptionLabelProp || defGetOptionLabel; // || resolvedColumn?.getOptionLabel!;

  // const getOptionValue = defGetOptionValue;
  // const getOptionLabel = defGetOptionLabel;

  const isOptionEqualToValue = useCallback(
    (option: ValueOptions, value: ValueOptions) => getOptionValue(option) === getOptionValue(value),
    [getOptionValue]
  );

  // const resolvedValueOptions = React.useMemo(() => ['flood', 'wind'], []);

  const resolvedValueOptions = useMemo(() => {
    if (!resolvedColumn?.valueOptions) {
      return [];
    }

    if (typeof resolvedColumn.valueOptions === 'function') {
      return resolvedColumn.valueOptions({ field: resolvedColumn.field });
    }

    return resolvedColumn.valueOptions;
  }, [resolvedColumn]);

  const resolvedFormattedValueOptions = useMemo(() => {
    return resolvedValueOptions?.map(getOptionValue);
  }, [resolvedValueOptions, getOptionValue]);

  // The value is computed from the item.value and used directly
  // If it was done by a useEffect/useState, the Autocomplete could receive incoherent value and options
  const filteredValues = useMemo(() => {
    if (!Array.isArray(item.value)) {
      return [];
    }
    if (resolvedValueOptions !== undefined) {
      const itemValueIndexes = item.value.map((element) => {
        // Gets the index matching between values and valueOptions
        return resolvedFormattedValueOptions?.findIndex(
          (formattedOption) => formattedOption === element
        );
      });

      return itemValueIndexes
        .filter((index) => index >= 0)
        .map((index: number) => resolvedValueOptions[index]);
    }
    return item.value;
  }, [item.value, resolvedValueOptions, resolvedFormattedValueOptions]);

  useEffect(() => {
    if (!Array.isArray(item.value) || filteredValues.length !== item.value.length) {
      // Updates the state if the filter value has been cleaned by the component
      applyValue({ ...item, value: filteredValues.map(getOptionValue) });
    }
  }, [item, filteredValues, applyValue, getOptionValue]);

  const handleChange = useCallback<
    NonNullable<AutocompleteProps<ValueOptions, true, false, true>['onChange']>
  >(
    (event, value) => {
      applyValue({ ...item, value: value.map(getOptionValue) });
    },
    [applyValue, item, getOptionValue]
  );

  return (
    (<Autocomplete<ValueOptions, true, false, true>
      multiple
      options={resolvedValueOptions}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={filter}
      id={id}
      value={filteredValues}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          // @ts-ignore
          (<rootProps.slots.baseChip
            variant='outlined'
            size='small'
            label={getOptionLabel(option)}
            {...getTagProps({ index })}
          />)
        ))
      }
      renderInput={(params) => (
        <rootProps.slots.baseTextField
          {...params}
          label={apiRef.current.getLocaleText('filterPanelInputLabel')}
          placeholder={apiRef.current.getLocaleText('filterPanelInputPlaceholder')}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
          inputRef={focusElementRef}
          type='singleSelect'
          {...TextFieldProps}
          {...rootProps.slotProps?.baseTextField}
        />
      )}
      {...other}
    />)
  );
};

// GridFilterInputMultipleMultipleSelect.propTypes = {
//   // ----------------------------- Warning --------------------------------
//   // | These PropTypes are generated from the TypeScript type definitions |
//   // | To update them edit the TypeScript types and run "yarn proptypes"  |
//   // ----------------------------------------------------------------------
//   apiRef: PropTypes.shape({
//     current: PropTypes.object.isRequired,
//   }).isRequired,
//   applyValue: PropTypes.func.isRequired /* @typescript-to-proptypes-ignore */,
//   focusElementRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
//   /**
//    * Used to determine the label displayed for a given value option.
//    * @param {ValueOptions} value The current value option.
//    * @returns {string} The text to be displayed.
//    */
//   getOptionLabel: PropTypes.func,
//   /**
//    * Used to determine the value used for a value option.
//    * @param {ValueOptions} value The current value option.
//    * @returns {string} The value to be used.
//    */
//   getOptionValue: PropTypes.func,
//   item: PropTypes.shape({
//     field: PropTypes.string.isRequired,
//     id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
//     operator: PropTypes.string.isRequired,
//     value: PropTypes.any,
//   }).isRequired,
//   type: PropTypes.oneOf(['multiSelect']),
// } as any;

// export { GridFilterInputMultipleMultipleSelect };
